import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Search, ChevronDown, User, ArrowRight } from "lucide-react";
import { CATEGORIES, CATEGORY_LABELS, SUBCATEGORIES, SUBCATEGORY_LABELS } from "../lib/constants";
import { useBrand } from "../context/BrandContext";
import { useAuth } from "../context/AuthContext";

// A white capsule floating inside a frosted band, inset from the viewport edges.
// Every child of the capsule is itself a pill — nothing squares off.
export function Navbar() {
    const brand = useBrand();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [q, setQ] = useState("");

    const doSearch = (e) => {
        e.preventDefault();
        if (q.trim()) {
            navigate(`/search?q=${encodeURIComponent(q.trim())}`);
            setSearchOpen(false);
            setMobileOpen(false);
            setQ("");
        }
    };

    return (
        <header className="sticky top-0 z-50 glass" data-testid="navbar">
            <div className="max-w-[1440px] mx-auto px-3 sm:px-5 lg:px-8 py-3">
                <div className="flex items-center justify-between gap-3 h-[62px] pl-3 pr-3 sm:pl-5 sm:pr-4 rounded-full bg-surface border border-line shadow-card">
                    <button
                        className="xl:hidden p-2 rounded-full text-dim hover:text-accent hover:bg-accent-soft transition-colors duration-200"
                        onClick={() => setMobileOpen((v) => !v)}
                        aria-label="Toggle menu"
                        data-testid="mobile-menu-toggle"
                    >
                        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                    </button>

                    <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="navbar-logo">
                        <span className="grid place-items-center w-9 h-9 rounded-full bg-accent-sheen shadow-glow">
                            <span className="w-3 h-3 rounded-full bg-surface" />
                        </span>
                        <span className="font-display text-lg sm:text-xl font-semibold tracking-[-0.01em] text-foreground">
                            {brand.site_name}
                        </span>
                    </Link>

                    {/* Primary nav — desktop */}
                    <nav className="hidden xl:flex items-center gap-0.5 flex-1 justify-center">
                        {CATEGORIES.map((cat) => (
                            <div key={cat} className="relative group">
                                <Link
                                    to={`/category/${cat}`}
                                    data-testid={`nav-cat-${cat}`}
                                    className="flex items-center gap-1 px-3.5 py-2 rounded-full text-[13px] font-medium text-dim hover:text-accent hover:bg-accent-soft transition-colors duration-200"
                                >
                                    {CATEGORY_LABELS[cat]}
                                    {cat === "personal-finance" && (
                                        <ChevronDown
                                            size={13}
                                            className="transition-transform duration-300 group-hover:rotate-180"
                                        />
                                    )}
                                </Link>
                                {cat === "personal-finance" && (
                                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-60 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300 z-50">
                                        <div className="panel p-2 shadow-lift">
                                            {SUBCATEGORIES.map((sub) => (
                                                <Link
                                                    key={sub}
                                                    to={`/category/personal-finance/${sub}`}
                                                    data-testid={`nav-sub-${sub}`}
                                                    className="group/sub flex items-center justify-between px-3.5 py-2.5 rounded-full text-[13px] font-medium text-dim hover:text-accent hover:bg-accent-soft transition-colors duration-200"
                                                >
                                                    {SUBCATEGORY_LABELS[sub]}
                                                    <ArrowRight size={13} className="text-accent opacity-0 -translate-x-1 transition-[opacity,transform] duration-200 group-hover/sub:opacity-100 group-hover/sub:translate-x-0" />
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        <Link
                            to="/archive"
                            data-testid="nav-archive"
                            className="px-3.5 py-2 rounded-full text-[13px] font-medium text-dim hover:text-accent hover:bg-accent-soft transition-colors duration-200"
                        >
                            Archive
                        </Link>
                    </nav>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setSearchOpen((v) => !v)}
                            className="grid place-items-center w-9 h-9 rounded-full bg-surface-raised text-dim hover:text-accent hover:bg-accent-soft transition-colors duration-200"
                            aria-label="Search"
                            data-testid="navbar-search-toggle"
                        >
                            <Search size={17} />
                        </button>
                        {user ? (
                            <div className="hidden sm:flex items-center gap-2">
                                <Link
                                    to="/account"
                                    data-testid="navbar-account"
                                    className="flex items-center gap-2 pl-1.5 pr-4 py-1.5 rounded-full bg-surface-raised text-[13px] font-medium text-foreground hover:bg-accent-soft transition-colors duration-200"
                                >
                                    <span className="grid place-items-center w-7 h-7 rounded-full bg-accent-sheen text-accent-foreground">
                                        <User size={13} />
                                    </span>
                                    {user.name?.split(" ")[0]}
                                </Link>
                                <button
                                    onClick={() => { logout(); navigate("/"); }}
                                    data-testid="navbar-logout"
                                    className="px-3.5 py-2 rounded-full text-[13px] font-medium text-dim hover:text-neg-ink hover:bg-neg/10 transition-colors duration-200"
                                >
                                    Log out
                                </button>
                            </div>
                        ) : (
                            <div className="hidden sm:flex items-center gap-2">
                                <Link
                                    to="/login"
                                    data-testid="navbar-login"
                                    className="px-3.5 py-2 rounded-full text-[13px] font-medium text-dim hover:text-accent hover:bg-accent-soft transition-colors duration-200"
                                >
                                    Log in
                                </Link>
                                <Link
                                    to="/register"
                                    data-testid="navbar-register"
                                    className="px-5 py-2.5 rounded-full bg-accent text-accent-foreground text-[13px] font-semibold shadow-card hover:shadow-glow hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300"
                                >
                                    Subscribe
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Search panel */}
            {searchOpen && (
                <div className="max-w-[1440px] mx-auto px-3 sm:px-5 lg:px-8 pb-3" data-testid="navbar-search-panel">
                    <form onSubmit={doSearch} className="panel p-3 flex flex-col sm:flex-row gap-2.5 animate-fade-up">
                        <div className="relative flex-1">
                            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-dim pointer-events-none" />
                            <input
                                autoFocus
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search articles, markets, authors..."
                                data-testid="navbar-search-input"
                                className="field pl-11 !rounded-full"
                            />
                        </div>
                        <button
                            type="submit"
                            data-testid="navbar-search-submit"
                            className="px-8 py-3 rounded-full bg-accent text-accent-foreground text-sm font-semibold shadow-card hover:shadow-glow transition-[box-shadow] duration-300"
                        >
                            Search
                        </button>
                    </form>
                </div>
            )}

            {/* Mobile menu */}
            {mobileOpen && (
                <nav className="xl:hidden max-w-[1440px] mx-auto px-3 sm:px-5 lg:px-8 pb-3" data-testid="mobile-menu">
                    <ul className="panel p-2.5 animate-fade-up">
                        {CATEGORIES.map((cat) => (
                            <li key={cat}>
                                <Link
                                    to={`/category/${cat}`}
                                    onClick={() => setMobileOpen(false)}
                                    className="flex items-center justify-between px-4 py-3 rounded-full text-sm font-medium text-foreground hover:text-accent hover:bg-accent-soft transition-colors duration-200"
                                >
                                    {CATEGORY_LABELS[cat]}
                                    <ArrowRight size={14} className="text-line-strong" />
                                </Link>
                            </li>
                        ))}
                        <li>
                            <Link
                                to="/archive"
                                onClick={() => setMobileOpen(false)}
                                className="flex items-center justify-between px-4 py-3 rounded-full text-sm font-medium text-dim hover:text-accent hover:bg-accent-soft transition-colors duration-200"
                            >
                                Archive
                                <ArrowRight size={14} className="text-line-strong" />
                            </Link>
                        </li>
                        {!user && (
                            <li className="flex gap-2.5 px-1.5 pt-3 mt-2 border-t border-line">
                                <Link to="/login" onClick={() => setMobileOpen(false)} className="flex-1 text-center py-3 rounded-full bg-surface-raised text-sm font-medium text-foreground">
                                    Log in
                                </Link>
                                <Link to="/register" onClick={() => setMobileOpen(false)} className="flex-1 text-center py-3 rounded-full bg-accent text-accent-foreground text-sm font-semibold shadow-card">
                                    Subscribe
                                </Link>
                            </li>
                        )}
                        {user && (
                            <li className="px-1.5 pt-3 mt-2 border-t border-line space-y-2.5">
                                <Link to="/account" onClick={() => setMobileOpen(false)} data-testid="mobile-account" className="block w-full text-center py-3 rounded-full bg-surface-raised text-sm font-medium text-foreground">
                                    My account
                                </Link>
                                <button onClick={() => { logout(); setMobileOpen(false); navigate("/"); }} className="w-full py-3 rounded-full bg-neg/10 text-sm font-medium text-neg-ink">
                                    Log out
                                </button>
                            </li>
                        )}
                    </ul>
                </nav>
            )}
        </header>
    );
}
